/**
 * Speaking detection — the pure part.
 *
 * Web Audio feeds it numbers; it answers "speaking or not" with hysteresis
 * and a hold, so the ring does not flicker on every syllable gap. Everything
 * here runs under vitest with no DOM.
 */

export interface SpeakingOptions {
  /** At or above this, speech begins. dBFS. */
  enterDb: number
  /** Below this — and only after holdMs of it — speech ends. dBFS. */
  exitDb: number
  /** How long the level must stay below exitDb before we stop. */
  holdMs: number
}

/** Plan Phase 3: enter at −45 dBFS, exit at −55 dBFS, 300 ms hold. */
export const DEFAULT_SPEAKING: SpeakingOptions = { enterDb: -45, exitDb: -55, holdMs: 300 }

export interface SpeakingDetector {
  /** Feed one level sample; returns the state after it. */
  update(db: number, now: number): boolean
  /** Force silence now — the instant the mic is muted, without waiting out the hold. */
  reset(): void
  readonly speaking: boolean
}

export function createSpeakingDetector(opts: SpeakingOptions = DEFAULT_SPEAKING): SpeakingDetector {
  let speaking = false
  /** When the level first dropped below exitDb while speaking; null while above it. */
  let quietSince: number | null = null

  return {
    get speaking() {
      return speaking
    },

    update(db, now) {
      if (!speaking) {
        if (db >= opts.enterDb) {
          speaking = true
          quietSince = null
        }
        return speaking
      }

      // Speaking. Between the two thresholds nothing changes — that gap is
      // the hysteresis. Below the exit threshold, the hold has to run out.
      if (db >= opts.exitDb) {
        quietSince = null
        return true
      }
      quietSince ??= now
      if (now - quietSince >= opts.holdMs) {
        speaking = false
        quietSince = null
      }
      return speaking
    },

    reset() {
      speaking = false
      quietSince = null
    },
  }
}

/**
 * RMS of a time-domain buffer (samples in −1..1) in dBFS.
 *
 * Silence is −Infinity, which compares below every threshold, as it should;
 * no special case is needed anywhere downstream.
 */
export function rmsDb(samples: ArrayLike<number>): number {
  if (samples.length === 0) return -Infinity
  let sum = 0
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i] ?? 0
    sum += s * s
  }
  const rms = Math.sqrt(sum / samples.length)
  return rms > 0 ? 20 * Math.log10(rms) : -Infinity
}

/**
 * dBFS to a 0..1 meter value, linear in dB from `floorDb` (0) to 0 dBFS (1).
 * −60 dBFS is where a quiet room sits on most laptops.
 */
export function levelFromDb(db: number, floorDb = -60): number {
  if (!Number.isFinite(db)) return 0
  const v = (db - floorDb) / -floorDb
  return v < 0 ? 0 : v > 1 ? 1 : v
}
